/**
 * 📝 REVIEWS & RATINGS MANAGEMENT DASHBOARD
 * Complete admin interface for managing driver and vehicle reviews
 * Backend: https://polarride-api-17dr.vercel.app/api/admin/reviews
 * Updated: Added Review Subject column with filter
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Rating,
  Avatar,
  Divider,
  Stack,
  Badge,
  LinearProgress,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  CheckCircle as ApproveIcon,
  Block as RemoveIcon,
  Flag as FlagIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
  StarRate as StarIcon,
  TrendingUp,
  TrendingDown,
  Report as ReportIcon,
  ThumbUp as HelpfulIcon,
  CalendarToday as DateIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { toast } from 'react-hot-toast';
import reviewsService, {
  type Review,
  type ReviewFilters,
  type ReviewStatistics,
  type ReviewReport,
} from '@/services/reviewsService';
import { simplBookingService } from '@/services/bookingService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const ReviewsDashboardPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);

  // Reviews State
  const [reviews, setReviews] = useState<Review[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalReviews, setTotalReviews] = useState(0);

  // Filters State
  const [filters, setFilters] = useState<ReviewFilters>({
    status: 'all',
    search: '',
  });
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [reviewSubjectFilter, setReviewSubjectFilter] = useState<'all' | 'vehicle' | 'driver' | 'renter'>('all');

  // Statistics State
  const [statistics, setStatistics] = useState<ReviewStatistics | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Review Details Modal
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);

  // Moderation Dialog
  const [moderationOpen, setModerationOpen] = useState(false);
  const [moderationAction, setModerationAction] = useState<'approve' | 'remove' | null>(null);
  const [removalReason, setRemovalReason] = useState('');

  // Database Initialization
  const [initLoading, setInitLoading] = useState(false);

  // Performance timing
  const startTimeRef = React.useRef<number>(0);

  // Caching for statistics
  const statisticsCacheRef = React.useRef<{
    data: ReviewStatistics | null;
    timestamp: number;
    dateRange: string;
  }>({ data: null, timestamp: 0, dateRange: '' });

  // Cache for review details
  const reviewDetailsCacheRef = React.useRef<Map<string, { review: Review; timestamp: number }>>(new Map());

  const STATS_CACHE_DURATION = 300000; // 5 minutes
  const REVIEW_DETAILS_CACHE_DURATION = 600000; // 10 minutes

  // OPTIMIZATION: Memoize filters to prevent unnecessary re-renders
  const memoizedFilters = React.useMemo(() => filters, [filters.status, filters.search, filters.bookingType, filters.rating]);

  // Load reviews
  useEffect(() => {
    loadReviews();
  }, [page, rowsPerPage, memoizedFilters, startDate, endDate]);

  // Load statistics when tab changes
  useEffect(() => {
    if (activeTab === 1) {
      loadStatistics();
    }
  }, [activeTab, startDate, endDate]);

  const loadReviews = async () => {
    startTimeRef.current = performance.now();

    try {
      setLoading(true);

      const startFetch = performance.now();

      const queryFilters: ReviewFilters = {
        ...filters,
        page: page + 1,
        limit: rowsPerPage,
        startDate: startDate?.toISOString().split('T')[0],
        endDate: endDate?.toISOString().split('T')[0],
      };

      // Fetch reviews from API (now includes booking status and dates)
      const result = await reviewsService.getReviews(queryFilters);

      const fetchTime = ((performance.now() - startFetch) / 1000).toFixed(2);

      if (result.success && result.data) {
        // Backend now provides bookingStatus, so we can filter based on that
        const validReviews = result.data.reviews.filter((review: Review) => {
          // Show all reviews - backend includes booking info
          // If bookingStatus is null, the booking might have been deleted, but we still show the review
          return true;
        });


        setReviews(validReviews);
        setTotalReviews(result.data.pagination?.total || validReviews.length);
      } else {
        toast.error(result.error || 'Failed to load reviews');
        setReviews([]);
        setTotalReviews(0);
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
      toast.error('Failed to load reviews');
      setReviews([]);
    } finally {
      setLoading(false);
      const loadTime = ((performance.now() - startTimeRef.current) / 1000).toFixed(2);
    }
  };

  const loadStatistics = async () => {
    // OPTIMIZATION: Check cache first
    const dateRange = `${startDate?.toISOString().split('T')[0] || ''}-${endDate?.toISOString().split('T')[0] || ''}`;

    if (statisticsCacheRef.current.data &&
        statisticsCacheRef.current.dateRange === dateRange &&
        (Date.now() - statisticsCacheRef.current.timestamp) < STATS_CACHE_DURATION) {
      setStatistics(statisticsCacheRef.current.data);
      return;
    }

    try {
      setStatsLoading(true);
      const startFetch = performance.now();

      const result = await reviewsService.getStatistics(
        startDate?.toISOString().split('T')[0],
        endDate?.toISOString().split('T')[0]
      );

      const fetchTime = ((performance.now() - startFetch) / 1000).toFixed(2);

      if (result.success && result.data) {
        setStatistics(result.data);
        // Cache the statistics
        statisticsCacheRef.current = {
          data: result.data,
          timestamp: Date.now(),
          dateRange
        };
      } else {
        toast.error(result.error || 'Failed to load statistics');
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
      toast.error('Failed to load statistics');
    } finally {
      setStatsLoading(false);
    }
  };

  const handleViewDetails = async (review: Review) => {
    // OPTIMIZATION: Check cache for review details
    const cached = reviewDetailsCacheRef.current.get(review.id);
    if (cached && (Date.now() - cached.timestamp) < REVIEW_DETAILS_CACHE_DURATION) {
      setSelectedReview(cached.review);
      setDetailsOpen(true);
      return;
    }

    setLoading(true);
    try {
      const startFetch = performance.now();

      // Fetch full review details including reports
      const result = await reviewsService.getReviewById(review.id);

      const fetchTime = ((performance.now() - startFetch) / 1000).toFixed(2);

      if (result.success && result.data) {
        setSelectedReview(result.data.review);
        setDetailsOpen(true);

        // Cache the review details
        reviewDetailsCacheRef.current.set(review.id, {
          review: result.data.review,
          timestamp: Date.now()
        });
      } else {
        toast.error(result.error || 'Failed to load review details');
      }
    } catch (error) {
      console.error('Error loading review details:', error);
      toast.error('Failed to load review details');
    } finally {
      setLoading(false);
    }
  };

  const handleModerate = (action: 'approve' | 'remove') => {
    setModerationAction(action);
    setModerationOpen(true);
  };

  const handleModerationSubmit = async () => {
    if (!selectedReview || !moderationAction) return;

    try {
      setLoading(true);
      const result = await reviewsService.moderateReview(
        selectedReview.id,
        moderationAction,
        removalReason || undefined
      );

      if (result.success) {
        toast.success(result.message || `Review ${moderationAction}d successfully!`);
        setModerationOpen(false);
        setDetailsOpen(false);
        setRemovalReason('');
        loadReviews(); // Refresh list
      } else {
        toast.error(result.error || 'Failed to moderate review');
      }
    } catch (error) {
      console.error('Error moderating review:', error);
      toast.error('Failed to moderate review');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof ReviewFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0); // Reset to first page
  };

  const handleResetFilters = () => {
    setFilters({ status: 'all', search: '' });
    setStartDate(null);
    setEndDate(null);
    setReviewSubjectFilter('all');
    setPage(0);
  };

  const handleInitRenterReviews = async () => {
    if (!confirm('Initialize Renter Reviews table? This is a one-time setup operation.')) {
      return;
    }

    try {
      setInitLoading(true);
      const result = await reviewsService.initRenterReviews();

      if (result.success) {
        toast.success(result.message || 'Renter reviews table initialized successfully!');
        // Refresh statistics to show new data
        if (activeTab === 1) {
          loadStatistics();
        }
      } else {
        toast.error(result.error || 'Failed to initialize renter reviews table');
      }
    } catch (error) {
      console.error('Error initializing renter reviews:', error);
      toast.error('Failed to initialize renter reviews table');
    } finally {
      setInitLoading(false);
    }
  };

  const getStatusChip = (status: string) => {
    const configs = {
      pending: { color: 'warning' as const, label: 'Pending' },
      published: { color: 'success' as const, label: 'Published' },
      active: { color: 'success' as const, label: 'Active' },
      removed: { color: 'error' as const, label: 'Removed' },
      flagged: { color: 'warning' as const, label: 'Flagged' },
    };
    const config = configs[status as keyof typeof configs] || { color: 'default' as const, label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const getBookingTypeIcon = (type: string) => {
    return type === 'vehicle' ? <CarIcon fontSize="small" /> : <PersonIcon fontSize="small" />;
  };

  const getReviewTypeLabel = (review: Review) => {
    if (review.bookingType === 'renter') {
      // Driver or Host reviewing Renter
      const reviewerLabel = review.reviewerType === 'driver' ? 'Driver' : 'Host';
      return (
        <Chip
          label={`${reviewerLabel} → Renter`}
          color="warning"
          size="small"
          variant="outlined"
        />
      );
    } else if (review.bookingType === 'driver') {
      // Renter reviewing Driver
      return (
        <Chip
          label="Renter → Driver"
          color="info"
          size="small"
          variant="outlined"
        />
      );
    } else {
      // Renter reviewing Vehicle/Host
      return (
        <Chip
          label="Renter → Vehicle"
          color="success"
          size="small"
          variant="outlined"
        />
      );
    }
  };

  const getReviewSubject = (review: Review) => {
    // Returns the subject being reviewed (vehicle, driver, or renter) with icon and name
    if (review.bookingType === 'vehicle' || review.targetType === 'vehicle') {
      return {
        icon: <CarIcon fontSize="small" color="success" />,
        name: review.vehicleName || review.targetName || 'Unknown Vehicle',
        type: 'Vehicle'
      };
    } else if (review.bookingType === 'driver' || review.targetType === 'driver') {
      return {
        icon: <PersonIcon fontSize="small" color="info" />,
        name: review.driverName || review.targetName || 'Unknown Driver',
        type: 'Driver'
      };
    } else if (review.bookingType === 'renter' || review.targetType === 'renter') {
      return {
        icon: <PersonIcon fontSize="small" color="warning" />,
        name: review.targetName || 'Unknown Renter',
        type: 'Renter'
      };
    }
    return {
      icon: <PersonIcon fontSize="small" />,
      name: 'Unknown',
      type: 'Unknown'
    };
  };

  // Filter reviews based on subject type
  const filteredReviews = React.useMemo(() => {
    if (reviewSubjectFilter === 'all') {
      return reviews;
    }
    return reviews.filter(review => {
      if (reviewSubjectFilter === 'vehicle') {
        return review.bookingType === 'vehicle' || review.targetType === 'vehicle';
      } else if (reviewSubjectFilter === 'driver') {
        return review.bookingType === 'driver' || review.targetType === 'driver';
      } else if (reviewSubjectFilter === 'renter') {
        return review.bookingType === 'renter' || review.targetType === 'renter';
      }
      return true;
    });
  }, [reviews, reviewSubjectFilter]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Reviews & Ratings Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage and moderate driver, vehicle, and renter reviews
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={handleInitRenterReviews}
              disabled={initLoading}
              sx={{ mt: 1 }}
            >
              {initLoading ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Initializing...
                </>
              ) : (
                'Initialize Renter Reviews'
              )}
            </Button>
          </Stack>
        </Box>

        <Paper sx={{ mb: 3 }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab label="All Reviews" icon={<StarIcon />} iconPosition="start" />
            <Tab label="Statistics" icon={<TrendingUp />} iconPosition="start" />
          </Tabs>
        </Paper>

        {/* Tab 1: All Reviews */}
        <TabPanel value={activeTab} index={0}>
          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={2.5}>
                <TextField
                  fullWidth
                  placeholder="Search reviews..."
                  value={filters.search || ''}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={1.5}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status || 'all'}
                    label="Status"
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="published">Published</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="removed">Removed</MenuItem>
                    <MenuItem value="flagged">Flagged</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Review Subject</InputLabel>
                  <Select
                    value={reviewSubjectFilter}
                    label="Review Subject"
                    onChange={(e) => setReviewSubjectFilter(e.target.value as 'all' | 'vehicle' | 'driver' | 'renter')}
                  >
                    <MenuItem value="all">All Subjects</MenuItem>
                    <MenuItem value="vehicle">Vehicle Reviews</MenuItem>
                    <MenuItem value="driver">Driver Reviews</MenuItem>
                    <MenuItem value="renter">Renter Reviews</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={1.5}>
                <FormControl fullWidth>
                  <InputLabel>Booking Type</InputLabel>
                  <Select
                    value={filters.bookingType || ''}
                    label="Booking Type"
                    onChange={(e) => handleFilterChange('bookingType', e.target.value as 'vehicle' | 'driver' | 'renter' | undefined)}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="vehicle">Vehicle</MenuItem>
                    <MenuItem value="driver">Driver</MenuItem>
                    <MenuItem value="renter">Renter</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Rating</InputLabel>
                  <Select
                    value={filters.rating || ''}
                    label="Rating"
                    onChange={(e) => handleFilterChange('rating', e.target.value as number | undefined)}
                  >
                    <MenuItem value="">All Ratings</MenuItem>
                    <MenuItem value={5}>5 Stars</MenuItem>
                    <MenuItem value={4}>4 Stars</MenuItem>
                    <MenuItem value={3}>3 Stars</MenuItem>
                    <MenuItem value={2}>2 Stars</MenuItem>
                    <MenuItem value={1}>1 Star</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={1.5}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(date) => setStartDate(date)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid item xs={12} md={1.5}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(date) => setEndDate(date)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={loadReviews}
                    fullWidth
                  >
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleResetFilters}
                  >
                    Reset
                  </Button>
                  <IconButton onClick={loadReviews} color="primary">
                    <RefreshIcon />
                  </IconButton>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {/* Reviews Table */}
          <Paper>
            {loading && <LinearProgress />}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Reviewer</TableCell>
                    <TableCell>Review Subject</TableCell>
                    <TableCell>Rating</TableCell>
                    <TableCell>Comment</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Helpful Count">
                        <HelpfulIcon fontSize="small" />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Report Count">
                        <ReportIcon fontSize="small" />
                      </Tooltip>
                    </TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredReviews.length === 0 && !loading ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          No reviews found. Try adjusting your filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredReviews.map((review) => {
                      const subject = getReviewSubject(review);
                      return (
                        <TableRow key={review.id} hover>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Avatar sx={{ width: 32, height: 32 }} src={review.reviewerAvatar}>
                                {review.reviewerName[0]}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {review.reviewerName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {review.reviewerEmail}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              {subject.icon}
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {subject.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {subject.type}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Rating value={review.rating} readOnly size="small" />
                              <Typography variant="body2">({review.rating})</Typography>
                            </Stack>
                          </TableCell>

                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {review.comment}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          {getReviewTypeLabel(review)}
                        </TableCell>

                        <TableCell align="center">
                          <Badge badgeContent={review.helpfulCount} color="primary">
                            <HelpfulIcon fontSize="small" color="action" />
                          </Badge>
                        </TableCell>

                        <TableCell align="center">
                          <Badge badgeContent={review.reportCount} color="error">
                            <ReportIcon fontSize="small" color="action" />
                          </Badge>
                        </TableCell>

                        <TableCell>{getStatusChip(review.status)}</TableCell>

                        <TableCell>
                          <Typography variant="caption">
                            {new Date(review.createdAt).toLocaleDateString()}
                          </Typography>
                        </TableCell>

                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(review)}
                            color="primary"
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalReviews}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </Paper>
        </TabPanel>

        {/* Tab 2: Statistics */}
        <TabPanel value={activeTab} index={1}>
          {statsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : statistics ? (
            <Grid container spacing={3}>
              {/* Overview Cards */}
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">
                      Total Reviews
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {(statistics?.totalReviews ?? 0).toLocaleString()}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                      <TrendingUp color="success" fontSize="small" />
                      <Typography variant="caption" color="success.main">
                        {statistics?.recentTrends?.last30Days ?? 0} this month
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">
                      Average Rating
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {(statistics?.averageRating ?? 0).toFixed(1)}
                    </Typography>
                    <Rating value={statistics?.averageRating ?? 0} readOnly size="small" sx={{ mt: 1 }} />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">
                      Active Reviews
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="success.main">
                      {(statistics?.activeReviews ?? 0).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      {statistics?.totalReviews ? ((statistics.activeReviews / statistics.totalReviews) * 100).toFixed(1) : 0}% of total
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">
                      Pending Reports
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="error.main">
                      {statistics?.pendingReports ?? statistics?.flaggedReviews ?? 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      Requires attention
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Rating Distribution */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Rating Distribution
                  </Typography>
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    {[5, 4, 3, 2, 1].map((rating) => (
                      <Box key={rating}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Typography variant="body2" sx={{ minWidth: 60 }}>
                            {rating} Stars
                          </Typography>
                          <Box sx={{ flexGrow: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={statistics?.totalReviews && statistics?.ratingDistribution ? ((statistics.ratingDistribution[rating as keyof typeof statistics.ratingDistribution] ?? 0) / statistics.totalReviews) * 100 : 0}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ minWidth: 60, textAlign: 'right' }}>
                            {statistics?.ratingDistribution?.[rating as keyof typeof statistics.ratingDistribution] ?? 0} ({statistics?.totalReviews ? (((statistics.ratingDistribution?.[rating as keyof typeof statistics.ratingDistribution] ?? 0) / statistics.totalReviews) * 100).toFixed(1) : 0}%)
                          </Typography>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              </Grid>

              {/* Review Types */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Review Types
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <CarIcon color="primary" />
                            <Typography variant="subtitle2">Vehicle Reviews</Typography>
                          </Stack>
                          <Typography variant="h5" fontWeight="bold">
                            {statistics.vehicleReviews.total.toLocaleString()}
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1 }}>
                            <Rating value={statistics.vehicleReviews.average} readOnly size="small" />
                            <Typography variant="caption">
                              ({statistics.vehicleReviews.average.toFixed(1)})
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <PersonIcon color="primary" />
                            <Typography variant="subtitle2">Driver Reviews</Typography>
                          </Stack>
                          <Typography variant="h5" fontWeight="bold">
                            {statistics.driverReviews.total.toLocaleString()}
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1 }}>
                            <Rating value={statistics.driverReviews.average} readOnly size="small" />
                            <Typography variant="caption">
                              ({statistics.driverReviews.average.toFixed(1)})
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <PersonIcon sx={{ color: 'warning.main' }} />
                            <Typography variant="subtitle2">Renter Reviews</Typography>
                          </Stack>
                          <Typography variant="h5" fontWeight="bold">
                            {statistics.renterReviews?.total?.toLocaleString() || 0}
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1 }}>
                            <Rating value={statistics.renterReviews?.average || 0} readOnly size="small" />
                            <Typography variant="caption">
                              ({statistics.renterReviews?.average?.toFixed(1) || '0.0'})
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="info">No statistics available</Alert>
          )}
        </TabPanel>

        {/* Review Details Dialog */}
        <Dialog
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Review Details</Typography>
              {selectedReview && getStatusChip(selectedReview.status)}
            </Stack>
          </DialogTitle>
          <DialogContent dividers>
            {selectedReview && (
              <Stack spacing={3}>
                {/* Review Info */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Review
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Rating value={selectedReview.rating} readOnly />
                    <Typography variant="h6">({selectedReview.rating}/5)</Typography>
                  </Stack>
                  <Typography variant="body1">{selectedReview.comment}</Typography>

                  {selectedReview.categories && Object.keys(selectedReview.categories).length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Category Ratings:
                      </Typography>
                      <Grid container spacing={1} sx={{ mt: 0.5 }}>
                        {Object.entries(selectedReview.categories).map(([category, rating]) => (
                          <Grid item xs={6} key={category}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="caption">
                                {category}:
                              </Typography>
                              <Rating value={rating} readOnly size="small" />
                            </Stack>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </Box>

                <Divider />

                {/* Reviewer Info */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Reviewer
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar src={selectedReview.reviewerAvatar}>
                      {selectedReview.reviewerName[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        {selectedReview.reviewerName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedReview.reviewerEmail}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Divider />

                {/* Target Info */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {selectedReview.bookingType === 'vehicle' ? 'Vehicle' : 'Driver'}
                  </Typography>
                  {selectedReview.bookingType === 'vehicle' ? (
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        {selectedReview.vehicleName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Host: {selectedReview.hostName}
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        {selectedReview.driverName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedReview.driverEmail}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Divider />

                {/* Booking Context */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Booking Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Booking ID
                      </Typography>
                      <Typography variant="body2">{selectedReview.bookingId}</Typography>
                    </Grid>
                    {selectedReview.bookingStartDate && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Start Date
                        </Typography>
                        <Typography variant="body2">
                          {new Date(selectedReview.bookingStartDate).toLocaleDateString()}
                        </Typography>
                      </Grid>
                    )}
                    {selectedReview.bookingEndDate && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          End Date
                        </Typography>
                        <Typography variant="body2">
                          {new Date(selectedReview.bookingEndDate).toLocaleDateString()}
                        </Typography>
                      </Grid>
                    )}
                    {selectedReview.bookingTotalCost && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Total Cost
                        </Typography>
                        <Typography variant="body2">
                          ₦{selectedReview.bookingTotalCost.toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>

                {/* Reports Section */}
                {selectedReview.reports && selectedReview.reports.length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Reports ({selectedReview.reports.length})
                      </Typography>
                      <Stack spacing={1}>
                        {selectedReview.reports.map((report) => (
                          <Alert
                            key={report.id}
                            severity={report.status === 'pending' ? 'warning' : 'info'}
                            action={
                              report.status === 'pending' && (
                                <Button size="small">Resolve</Button>
                              )
                            }
                          >
                            <Typography variant="body2" fontWeight={500}>
                              {report.reason}
                            </Typography>
                            {report.additionalInfo && (
                              <Typography variant="caption" display="block">
                                {report.additionalInfo}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              Reported by: {report.reporterName || report.reporterEmail} on{' '}
                              {new Date(report.createdAt).toLocaleDateString()}
                            </Typography>
                          </Alert>
                        ))}
                      </Stack>
                    </Box>
                  </>
                )}

                {/* Engagement Metrics */}
                <Divider />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Engagement
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <HelpfulIcon color="primary" />
                        <Typography variant="body2">
                          {selectedReview.helpfulCount} found helpful
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ReportIcon color="error" />
                        <Typography variant="body2">
                          {selectedReview.reportCount} reports
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsOpen(false)}>Close</Button>
            {selectedReview && selectedReview.status === 'removed' && (
              <Button
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                onClick={() => handleModerate('approve')}
              >
                Approve
              </Button>
            )}
            {selectedReview && selectedReview.status !== 'removed' && (
              <Button
                variant="contained"
                color="error"
                startIcon={<RemoveIcon />}
                onClick={() => handleModerate('remove')}
              >
                Remove
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Moderation Dialog */}
        <Dialog
          open={moderationOpen}
          onClose={() => setModerationOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {moderationAction === 'approve' ? 'Approve Review' : 'Remove Review'}
          </DialogTitle>
          <DialogContent>
            {moderationAction === 'remove' ? (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This review will be hidden from public view. Please provide a reason.
                </Alert>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Removal Reason"
                  value={removalReason}
                  onChange={(e) => setRemovalReason(e.target.value)}
                  placeholder="Enter the reason for removing this review..."
                />
              </>
            ) : (
              <Alert severity="success">
                This review will be reinstated and visible to users.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setModerationOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color={moderationAction === 'approve' ? 'success' : 'error'}
              onClick={handleModerationSubmit}
              disabled={moderationAction === 'remove' && !removalReason.trim()}
            >
              Confirm {moderationAction === 'approve' ? 'Approval' : 'Removal'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default ReviewsDashboardPage;
